import {
  renderGrid,
  renderXAxisLabels,
  renderYaxisNumerical,
  renderVerticalStripDividers,
} from '../../zoompan-canvas/renderAxes';
import { getBoundData, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
  TpZoomPanelCoordMapper,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTable, TpDataTableColumn, VALTYPES } from '../../../../data/interfaces';
import { getStandardDataColor } from '../../../themes/themes';
import { renderLegendItems, renderYLabel, TpLegendItem } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';
import {
  createColorMappingInfo,
  getMappedColorDataPoint,
  getMappedColorLegendInfo,
  getMappedColorStringDataPoint,
} from '../../helpers/colorMappingInfo';
import { chartDims, generateChartTitle, isSelectedItem } from '../../helpers/misc';
import { getThemedGrayFraction } from '../../../themes/themes';
import { getLabelSpaceSize } from '../../helpers/configParams';
import { getColumnInfo } from '../../../../data/analyticsDataSlice';
import { TpHoverInfo, TpHoverRenderContext } from '../../zoompan-canvas/ZoomPanCanvas';
import { getUserError } from '../../../../components/miscUtils';
import { byNumericKey, noOpSorter } from '../../../../components/sortUtils';
import { mulberry32 } from '../../../../components/randomGenerators';
import { PERFORMANCE_POINT_COUNT_THRESHOLD } from '../config';
import { TpDashboardGlobalChartConfig } from '../../dashboard/interfaces';
import { clipToRenderedRange } from '../../zoompan-canvas/helpers';

interface TpCoordinateInfo {
  columnInfo: TpDataTableColumn;
  data: number[];
  minVal: number;
  maxVal: number;
  sumVal: number;
  averageVal: number;
}

/**
 * Returns the sorting function for the coordinates based on the selected sortBy option.
 */
function coordinateSortFunction(
  sortBy: 'user' | 'avgValInc' | 'avgValDec',
): (a: TpCoordinateInfo, b: TpCoordinateInfo) => number {
  if (sortBy === 'user') return noOpSorter;
  if (sortBy === 'avgValInc') return byNumericKey('averageVal', true);
  if (sortBy === 'avgValDec') return byNumericKey('averageVal', false);
  return noOpSorter;
}

/**
 * Create a horizontal coordinate data provider for parallel coordinates plot.
 * @param itemCount number of items in the table
 * @param coordCount number of coordinates
 * @param spread whether to spread the coordinates or collapse them
 * @param seed fixed seed for the random generator
 * @returns function to get the horizontal coordinate value for a given item and coordinate index
 */
function createHorizontalCoordinateData(itemCount: number, coordCount: number, spread: boolean, seed = 0) {
  if (coordCount === 0) {
    // Dummy case: no coordinates
    return (_itemNr: number, _coordIdx: number) => 0;
  }

  if (!spread) {
    // Collapsed mode: all coordinates centered on the middle of their slot
    return (_itemNr: number, _coordIdx: number) => (_coordIdx + 0.5) / coordCount;
  }

  // Spread mode: add random jitter offsets per coordinate and item
  const rnd = mulberry32(seed);
  const offsets = new Float32Array(coordCount * itemCount);
  for (let coordIdx = 0; coordIdx < coordCount; coordIdx++) {
    for (let itemNr = 0; itemNr < itemCount; itemNr++) {
      offsets[coordIdx * itemCount + itemNr] = 0.2 + 0.6 * rnd();
    }
  }

  return (itemNr: number, coordIdx: number) => (coordIdx + offsets[coordIdx * itemCount + itemNr]) / coordCount;
}

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
  widgetGlobalConfig: TpDashboardGlobalChartConfig,
) {
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const dataTable: TpDataTable = boundData.dataTable;

  const dataHoverLabel = boundData.bindings.hoverLabel?.data;
  const title = generateChartTitle(dataContext, dataBinding, config, 'Parallel coordinates');
  const dataItemId = boundData.bindings.itemId.data;
  const commonAxes = config.commonAxes;
  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;
  const getItemIndexById = boundData.getItemIndexById;

  const opacity = config.opacity;
  const unselectedOpacity = config.unselectedOpacity;
  const pointSizeFactor = config.pointSizeFactor;
  const globalColorMappingConfig = widgetGlobalConfig.colorMapping;
  const colorMappingInfo = createColorMappingInfo(config, boundData, 'color', opacity, globalColorMappingConfig);

  const lineMode = config.lineMode || 'all';
  const drawCustomHover = ['highlighted', 'all'].includes(lineMode);

  const coordinateCount = boundData.compoundBindings.coordinates?.length || 0;
  const totalPoints = dataItemId.length * coordinateCount;
  // If we have a very large number of points, we switch to a faster rendering mode
  // by reducing the point size factor and disabling some visual features
  const performanceMode =
    config.qualityMode === 'performance' ||
    (config.qualityMode === 'auto' && totalPoints > PERFORMANCE_POINT_COUNT_THRESHOLD);

  const scatterXAxis = config.xaxisspread === 'spread';
  const getHorzCoordValue = createHorizontalCoordinateData(dataItemId.length, coordinateCount, scatterXAxis);

  const coordinates: TpCoordinateInfo[] = boundData.compoundBindings.coordinates
    .map((coordInp) => {
      if (coordInp.columnInfo.valueType !== VALTYPES.NUMBER)
        throw getUserError(`Parallel coordinate should be numerical: ${coordInp.columnInfo.id}`);
      let minVal = Number.MAX_VALUE;
      let maxVal = -Number.MAX_VALUE;
      let sumVal = 0;
      let countVal = 0;
      for (const val of coordInp.data) {
        if (!isNaN(val)) {
          if (val < minVal) minVal = val;
          if (val > maxVal) maxVal = val;
          sumVal += val;
          countVal++;
        }
      }
      const coord: TpCoordinateInfo = {
        columnInfo: coordInp.columnInfo,
        data: coordInp.data,
        minVal,
        maxVal,
        sumVal,
        averageVal: countVal > 0 ? sumVal / countVal : NaN,
      };
      return coord;
    })
    .sort(coordinateSortFunction(config.sortBy || 'user'));

  let globalMinVal;
  let globalMaxVal;
  if (!commonAxes) {
    globalMinVal = 0;
    globalMaxVal = 1;
  } else {
    globalMinVal = 1e99;
    globalMaxVal = -1e99;
    for (const coord of coordinates) {
      if (coord.minVal < globalMinVal) globalMinVal = coord.minVal;
      if (coord.maxVal > globalMaxVal) globalMaxVal = coord.maxVal;
    }
  }

  const hasLegend = colorMappingInfo.hasColors;
  const legendBottomSize = hasLegend ? chartDims.legendHeight : 0;

  const padding = chartDims.padding;

  const xAxisHeight = getLabelSpaceSize(config.labelSpace);

  const zpLogicalRangeX = createLogicalRange(0, 1);
  const range = globalMaxVal - globalMinVal;
  const zpLogicalRangeY = createLogicalRange(globalMinVal - range * 0.05, globalMaxVal + range * 0.05);
  const zpRenderRangeX = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.yAxisWidth),
    RenderMaxDiffAbs(padding + 0),
  );
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.headerHeight),
    RenderMaxDiffAbs(padding + xAxisHeight + legendBottomSize),
  );

  function renderColumnLabels(renderContext: TpAreaRenderContext) {
    renderXAxisLabels(
      renderContext,
      coordinates.map((coord) => coord.columnInfo.name),
    );
  }

  const standardFillColor = getStandardDataColor().opacity(opacity).toCanvasString();
  const grayColor = getThemedGrayFraction(0.5)
    .opacity(opacity * unselectedOpacity)
    .toCanvasString();
  const grayColorFill = getThemedGrayFraction(0.5)
    .opacity(opacity * unselectedOpacity * 0.5)
    .toCanvasString();

  function renderLines(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;
    const pointSizeRect = Math.round(pixelConv(2.5 * pointSizeFactor));

    renderGrid(renderContext, false, commonAxes);
    if (scatterXAxis) {
      renderVerticalStripDividers(coordinates.length, renderContext);
    }

    for (let rowNr = 0; rowNr < dataItemId.length; rowNr++) {
      let firstPoint = true;

      if (colorMappingInfo.hasColors) {
        const [fillColorStr, strokeColorStr] = getMappedColorStringDataPoint(colorMappingInfo, rowNr);
        ctx.fillStyle = fillColorStr;
        ctx.strokeStyle = strokeColorStr;
      } else {
        ctx.fillStyle = standardFillColor;
        ctx.strokeStyle = standardFillColor;
      }
      ctx.lineWidth = renderContext.pixelRatio * 0.75;

      if (!isSelectedItem(selectedItemsMap, dataItemId[rowNr])) {
        ctx.fillStyle = grayColorFill;
        ctx.strokeStyle = grayColor;
        ctx.lineWidth = renderContext.pixelRatio * 0.5;
      }

      if (lineMode === 'all') {
        ctx.beginPath();
        for (const [coordIdx, coord] of coordinates.entries()) {
          const value = coord.data[rowNr];
          if (!isNaN(value) && (coord.maxVal > coord.minVal || commonAxes)) {
            let valueCorr = value;
            if (!commonAxes) valueCorr = (value - coord.minVal) / (coord.maxVal - coord.minVal);
            const scr_x = mapx(getHorzCoordValue(rowNr, coordIdx));
            const scr_y = mapy(valueCorr);
            if (firstPoint) ctx.moveTo(scr_x, scr_y);
            else ctx.lineTo(scr_x, scr_y);
            firstPoint = false;
          } else firstPoint = true;
        }
        ctx.stroke();
      }

      for (const [coordIdx, coord] of coordinates.entries()) {
        const value = coord.data[rowNr];
        if (!isNaN(value) && (coord.maxVal > coord.minVal || commonAxes)) {
          let valueCorr = value;
          if (!commonAxes) valueCorr = (value - coord.minVal) / (coord.maxVal - coord.minVal);
          const scr_x = mapx(getHorzCoordValue(rowNr, coordIdx));
          const scr_y = mapy(valueCorr);
          if (performanceMode) {
            ctx.fillRect(
              Math.round(scr_x - pointSizeRect / 2),
              Math.round(scr_y - pointSizeRect / 2),
              pointSizeRect,
              pointSizeRect,
            );
          } else {
            const pointSize = 3;
            ctx.beginPath();
            ctx.arc(scr_x, scr_y, pixelConv(pointSize * pointSizeFactor), 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();
          }
        }
      }
    }
  }

  function renderLegend(renderContext: TpAreaRenderContext) {
    const legendItems: TpLegendItem[] = [];
    if (colorMappingInfo.hasColors) legendItems.push(getMappedColorLegendInfo(colorMappingInfo));
    renderLegendItems(renderContext, legendItems);
  }

  function customHoverRenderer(
    hoverInfo: TpHoverInfo,
    hoverContext: TpHoverRenderContext,
    ctx: CanvasRenderingContext2D,
    mapX: TpZoomPanelCoordMapper,
    mapY: TpZoomPanelCoordMapper,
    pixelRatio: number,
  ) {
    clipToRenderedRange(ctx, mapX, mapY);

    ctx.lineWidth = pixelRatio * 3.75;
    const rowNr = getItemIndexById(hoverInfo.itemId);

    let firstPoint = true;

    if (colorMappingInfo.hasColors) {
      const fillColorStr = getMappedColorDataPoint(colorMappingInfo, rowNr).toCanvasString();
      ctx.fillStyle = fillColorStr;
      ctx.strokeStyle = fillColorStr;
    } else {
      ctx.fillStyle = standardFillColor;
      ctx.strokeStyle = standardFillColor;
    }
    ctx.lineWidth = pixelRatio * 0.75 * 5;

    if (!isSelectedItem(selectedItemsMap, dataItemId[rowNr])) {
      ctx.fillStyle = grayColorFill;
      ctx.strokeStyle = grayColor;
      ctx.lineWidth = pixelRatio * 0.5 * 5;
    }

    ctx.beginPath();
    for (const [coordIdx, coord] of coordinates.entries()) {
      const value = coord.data[rowNr];
      if (!isNaN(value) && (coord.maxVal > coord.minVal || commonAxes)) {
        let valueCorr = value;
        if (!commonAxes) valueCorr = (value - coord.minVal) / (coord.maxVal - coord.minVal);
        const scr_x = mapX.map(getHorzCoordValue(rowNr, coordIdx));
        const scr_y = mapY.map(valueCorr);
        if (firstPoint) {
          ctx.moveTo(scr_x, scr_y);
        } else {
          ctx.lineTo(scr_x, scr_y);
        }
        firstPoint = false;
      } else {
        // If the line mode is 'highlighted' we want to draw the line also for missing values,
        // so we just skip the point but keep connecting the line
        firstPoint = lineMode === 'all';
      }
    }
    ctx.stroke();

    for (const [coordIdx, coord] of coordinates.entries()) {
      const value = coord.data[rowNr];
      if (!isNaN(value) && (coord.maxVal > coord.minVal || commonAxes)) {
        let valueCorr = value;
        if (!commonAxes) valueCorr = (value - coord.minVal) / (coord.maxVal - coord.minVal);
        const scr_x = mapX.map(getHorzCoordValue(rowNr, coordIdx));
        const scr_y = mapY.map(valueCorr);
        ctx.beginPath();
        ctx.arc(scr_x, scr_y, 6 * pixelRatio, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  const areaLines: TpChartAreaDefinition = {
    id: 'lines',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    canAreaSelect: 'y',
    render: renderLines,
  };

  const chartAreas: TpChartAreaDefinition[] = [
    areaLines,
    {
      id: 'xaxis',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + xAxisHeight + legendBottomSize),
        RenderMaxDiffAbs(padding + legendBottomSize),
      ),
      zoomPanX: true,
      render: renderColumnLabels,
    },
    {
      id: 'ylabel',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + 0),
        RenderMinDiffAbs(padding + chartDims.headerHeight),
      ),
      render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, '', title),
    },
  ];

  if (commonAxes) {
    chartAreas.push({
      id: 'yaxis',
      logicalRangeX: createLogicalRange(0, 1),
      logicalRangeY: zpLogicalRangeY,
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMinDiffAbs(padding + chartDims.yAxisWidth)), // offset of Y=4 to make axes overlap
      renderRangeY: zpRenderRangeY,
      zoomPanY: true,
      render: renderYaxisNumerical,
    });
  }

  if (hasLegend) {
    chartAreas.push({
      id: 'legend',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSize), RenderMaxDiffAbs(padding)),
      render: renderLegend,
    });
  }

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
  };

  return {
    definition,
    areaLines,
    dataTable,
    colInfoHover: boundData.bindings.hoverLabel?.columnId
      ? getColumnInfo(dataTable, boundData.bindings.hoverLabel?.columnId)
      : null,
    dataHoverLabel,
    dataItemId,
    coordinates,
    commonAxes,
    getItemIndexById: boundData.getItemIndexById,
    customHoverRenderer: drawCustomHover ? customHoverRenderer : undefined,
    title,
    getHorzCoordValue,
  };
}
